import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ A read whose skeleton follows WHAT is being shown, not just the first load.│
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * ⚠️⚠️ **14 Oct 2026, from UAT: the dashboards drew their EMPTY state for seven seconds.**
 * `useFirstLoad` ends its skeleton when the first request settles — but a dashboard's reload is
 * rebuilt whenever the session object changes, so the first, superseded request settled early and
 * the screen sat on *"no people in this view"* while the real one was still running. And a period
 * stepped later showed the previous period's rows under the new dates.
 *
 * So the skeleton is keyed: `shape` names what the screen is asked to show (the period, the mode,
 * the filters that change the rows), and `loading` stays true until data for THAT shape has landed.
 * A live refresh keeps the same shape and draws no skeleton; a step to another period does.
 *
 * ⚠️ **A late answer is dropped.** Every run gets a sequence number and `load` is told whether it
 * is still the latest, so a slow response for last week cannot overwrite this week's.
 *
 * ⚠️ Leave out of `shape` what changes on every keystroke (a search box): the rows refresh as before
 * without a skeleton flashing per letter.
 */
export function useKeyedLoad(
  shape: string,
  load: (isLatest: () => boolean) => Promise<void>,
): { readonly loading: boolean; readonly reload: () => Promise<void> } {
  const sequence = useRef(0);
  const [loadedShape, setLoadedShape] = useState<string | null>(null);

  const reload = useCallback(async (): Promise<void> => {
    sequence.current += 1;
    const mine = sequence.current;
    const isLatest = (): boolean => sequence.current === mine;
    await load(isLatest);
    if (isLatest()) setLoadedShape(shape);
  }, [load, shape]);

  useEffect(() => {
    void reload();
  }, [reload]);

  return { loading: loadedShape !== shape, reload };
}
